import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaCheck, FaTimes, FaCreditCard, FaCalendarAlt, FaClock, FaUser, FaEnvelope, FaInfoCircle, FaHistory } from 'react-icons/fa';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { firestore, functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { formatCurrency, formatDate, getRemainingDays } from '../utils/formatters';

// 样式组件
const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 0 20px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 30px;
  color: #333;
  text-align: center;
`;

const NoSubscriptionMessage = styled.div`
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  margin: 40px 0;
`;

const NoSubscriptionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
`;

const NoSubscriptionText = styled.p`
  font-size: 1rem;
  color: #666;
  margin-bottom: 24px;
`;

const SubscribeButton = styled.button`
  background-color: #4f46e5;
  color: white;
  font-weight: 600;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #4338ca;
  }
`;

const SubscriptionCard = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 30px;
  margin-bottom: 30px;
`;

const PlanName = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 10px;
  background-color: ${({ status }) => {
    switch (status) {
      case 'active':
        return '#22c55e';
      case 'canceled':
        return '#f97316';
      case 'past_due':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  }};
  color: white;
`;

const PriceInfo = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: #4f46e5;
  margin: 12px 0;
`;

const PeriodInfo = styled.div`
  display: flex;
  align-items: center;
  color: #666;
  margin: 6px 0;
  font-size: 0.9rem;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  margin-right: 8px;
  color: #4f46e5;
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin: 30px 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 30px;
`;

const ActionButton = styled.button<{ primary?: boolean }>`
  background-color: ${props => props.primary ? '#4f46e5' : 'white'};
  color: ${props => props.primary ? 'white' : '#4f46e5'};
  border: 1px solid ${props => props.primary ? 'transparent' : '#4f46e5'};
  padding: 10px 16px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: ${props => props.primary ? '#4338ca' : 'rgba(79, 70, 229, 0.05)'};
  }
`;

const UserInfoSection = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 30px;
  margin-bottom: 30px;
`;

const getStatusText = (status: string, language: string): string => {
  if (!status) return language === 'zh' ? '未知' : 'Unknown';
  
  switch (status) {
    case 'active':
      return language === 'zh' ? '活跃' : 'Active';
    case 'active_canceling':
      return language === 'zh' ? '将在期末取消' : 'Canceling at Period End';
    case 'canceled':
      return language === 'zh' ? '已取消' : 'Canceled';
    case 'past_due':
      return language === 'zh' ? '逾期' : 'Past Due';
    case 'incomplete':
      return language === 'zh' ? '未完成' : 'Incomplete';
    case 'incomplete_expired':
      return language === 'zh' ? '已过期' : 'Expired';
    case 'trialing':
      return language === 'zh' ? '试用中' : 'Trial';
    case 'unpaid':
      return language === 'zh' ? '未支付' : 'Unpaid';
    default:
      return status;
  }
};

const getPlanName = (planId: string, interval: string, language: string) => {
  if (!planId) return language === 'zh' ? '未知方案' : 'Unknown Plan';
  
  if (planId.includes('pro') && interval === 'month') {
    return language === 'zh' ? '专业版（月付）' : 'Pro (Monthly)';
  } else if (planId.includes('pro') && interval === 'year') {
    return language === 'zh' ? '专业版（年付）' : 'Pro (Yearly)';
  } else if (planId.includes('pro') && (interval === 'lifetime' || interval === undefined)) {
    return language === 'zh' ? '专业版（终身）' : 'Pro (Lifetime)';
  }
  
  return language === 'zh' ? '标准方案' : 'Standard Plan';
};

// 判断订阅是否为终身版本
const isLifetimeSubscription = (subscription: any) => {
  return subscription.is_lifetime === true || 
         subscription.plan_interval === 'lifetime' || 
         subscription.payment_mode === 'one-time';
};

const MySubscription: React.FC = () => {
  const [subscription, setSubscription] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [redirectLoading, setRedirectLoading] = useState(false);
  
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // 检查用户是否已登录
  useEffect(() => {
    if (!authLoading) {
      setAuthChecked(true);
    }
  }, [authLoading]);

  // 获取用户的订阅信息
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        // 直接调用后端 API 获取用户订阅信息
        const response = await fetch(`https://get-user-subscription-4lwrmzn6aa-uc.a.run.app?user_id=${user.uid}`);
        
        if (!response.ok) {
          throw new Error(`服务器响应错误: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('订阅数据:', data); // 添加日志，帮助调试
        
        if (data.exists && data.subscription) {
          setSubscription(data.subscription);
        } else {
          setSubscription(null);
        }
      } catch (error) {
        console.error('获取订阅信息失败:', error);
        setError('无法加载您的订阅信息，请稍后再试。');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchSubscription();
    }
  }, [user]);

  // 如果认证检查完成且用户未登录，重定向到登录页
  if (authChecked && !authLoading && !user) {
    return <Navigate to="/login" />;
  }

  // 处理查看定价页面
  const handleViewPlans = () => {
    navigate('/pricing');
  };
  
  // 处理查看客户门户
  const handleManageSubscription = async () => {
    if (!subscription || !subscription.customer_id) {
      setError(t('noCustomerId'));
      return;
    }
    
    try {
      setRedirectLoading(true);
      // 调用我们的后端API创建门户会话
      const response = await fetch('https://create-portal-session-by-customer-id-4lwrmzn6aa-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_id: subscription.customer_id,
          return_url: window.location.origin + '/mySubscription',
        }),
      });
      
      if (!response.ok) {
        throw new Error(`${t('serverError')}: ${response.status}`);
      }
      
      const data = await response.json();
      
      // 跳转到Stripe门户URL
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(t('noPortalUrl'));
      }
    } catch (error) {
      console.error('打开订阅管理门户失败:', error);
      setError(t('portalOpenFailed'));
    } finally {
      setRedirectLoading(false);
    }
  };

  // 在已有条件下添加，识别 lifetime 订阅
  const isLifetime = subscription && isLifetimeSubscription(subscription);

  if (loading) {
    return (
      <Container>
        <Title>{t('loading')}</Title>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Title>{t('mySubscription')}</Title>
        <NoSubscriptionMessage>
          <NoSubscriptionTitle>{t('error')}</NoSubscriptionTitle>
          <NoSubscriptionText>{error}</NoSubscriptionText>
        </NoSubscriptionMessage>
      </Container>
    );
  }
  
  if (!user) {
    return (
      <Container>
        <Title>{t('mySubscription')}</Title>
        <NoSubscriptionMessage>
          <NoSubscriptionTitle>{t('pleaseSignIn')}</NoSubscriptionTitle>
          <NoSubscriptionText>
            {t('signInToViewSubscription')}
          </NoSubscriptionText>
          <SubscribeButton onClick={() => navigate('/login')}>
            {t('signIn')}
          </SubscribeButton>
        </NoSubscriptionMessage>
      </Container>
    );
  }

  if (!subscription) {
    return (
      <Container>
        <Title>{t('mySubscription')}</Title>
        <NoSubscriptionMessage>
          <NoSubscriptionTitle>{t('noActiveSubscription')}</NoSubscriptionTitle>
          <NoSubscriptionText>
            {language === 'zh' 
              ? '开始免费试用，体验所有专业功能，包括 Markdown 预览、LaTeX 公式渲染、JSON 格式化等。'
              : 'Start your free trial to experience all Pro features including Markdown preview, LaTeX formula rendering, JSON formatting, and more.'}
          </NoSubscriptionText>
          <SubscribeButton onClick={handleViewPlans}>
            {language === 'zh' ? '开始免费试用' : 'Start Free Trial'}
          </SubscribeButton>
        </NoSubscriptionMessage>
      </Container>
    );
  }

  // 判断是否在试用期内
  const isInTrialPeriod = subscription.is_trial && subscription.trial_end && 
    (new Date(subscription.trial_end * 1000) > new Date());

  // 判断是否是试用已过期但未订阅
  const isTrialExpired = subscription.is_trial && subscription.trial_end && 
    (new Date(subscription.trial_end * 1000) <= new Date()) && 
    subscription.status !== 'active';

  // 如果试用已过期且未订阅
  if (isTrialExpired) {
    return (
      <Container>
        <Title>{t('mySubscription')}</Title>
        <NoSubscriptionMessage>
          <NoSubscriptionTitle>
            {language === 'zh' ? '试用期已结束' : 'Trial Period Ended'}
          </NoSubscriptionTitle>
          <NoSubscriptionText>
            {language === 'zh' 
              ? '您的免费试用期已结束。订阅专业版继续使用所有功能，包括 Markdown 预览、LaTeX 公式渲染、JSON 格式化等。'
              : 'Your free trial has ended. Subscribe to Pro to continue using all features including Markdown preview, LaTeX formula rendering, JSON formatting, and more.'}
          </NoSubscriptionText>
          <SubscribeButton onClick={handleViewPlans}>
            {language === 'zh' ? '升级到专业版' : 'Upgrade to Pro'}
          </SubscribeButton>
        </NoSubscriptionMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Title>{t('mySubscription')}</Title>
      
      <UserInfoSection>
        <SectionTitle>{t('accountInformation')}</SectionTitle>
        
        <PeriodInfo>
          <IconWrapper>
            <FaUser />
          </IconWrapper>
          {t('name')}: {subscription.customer_name || user.displayName || t('unknown')}
        </PeriodInfo>
        
        <PeriodInfo>
          <IconWrapper>
            <FaEnvelope />
          </IconWrapper>
          {t('email')}: {subscription.customer_email || user.email || t('unknown')}
        </PeriodInfo>
      </UserInfoSection>
      
      <SubscriptionCard>
        <PlanName>
          {subscription.plan_name || getPlanName(subscription.plan_id, subscription.plan_interval, language)}
          <StatusBadge status={subscription.status || subscription.subscription_status}>
            {getStatusText(subscription.status || subscription.subscription_status, language)}
          </StatusBadge>
        </PlanName>
        
        <PriceInfo>
          {subscription.plan_amount && (
            `${formatCurrency(subscription.plan_amount || subscription.amount_total, subscription.currency)} ${isLifetime ? `(${t('oneTimePayment')})` : `/ ${subscription.plan_interval === 'month' ? t('month') : t('year')}`}`
          )}
        </PriceInfo>
        
        {!isLifetime ? (
          <PeriodInfo>
            <IconWrapper>
              <FaCalendarAlt />
            </IconWrapper>
            {t('currentPeriod')}: {formatDate(subscription.current_period_start, language)} - {formatDate(subscription.current_period_end, language)}
          </PeriodInfo>
        ) : (
          <PeriodInfo>
            <IconWrapper>
              <FaCalendarAlt />
            </IconWrapper>
            {t('lifetimeAccess')}
          </PeriodInfo>
        )}
        
        {isInTrialPeriod && (
          <PeriodInfo>
            <IconWrapper>
              <FaClock />
            </IconWrapper>
            {language === 'zh' 
              ? `试用期还剩 ${getRemainingDays(subscription.trial_end)} 天`
              : `${getRemainingDays(subscription.trial_end)} days left in trial`}
          </PeriodInfo>
        )}
        
        {!isInTrialPeriod && !isLifetime && (
          <PeriodInfo>
            <IconWrapper>
              <FaCreditCard />
            </IconWrapper>
            {subscription.cancel_at_period_end
              ? t('willCancelAtPeriodEnd')
              : t('willAutoRenew')}
          </PeriodInfo>
        )}
        
        <PeriodInfo>
          <IconWrapper>
            <FaClock />
          </IconWrapper>
          {t('subscriptionStartedOn')}: {formatDate(subscription.created_at, language)}
        </PeriodInfo>
        
        {/* 购买日期显示 - 仅限 lifetime */}
        {isLifetime && subscription.payment_completed_at && (
          <PeriodInfo>
            <IconWrapper>
              <FaHistory />
            </IconWrapper>
            {t('purchaseDate')}: {formatDate(subscription.payment_completed_at, language)}
          </PeriodInfo>
        )}
        
        {/* 按钮区域 - 针对不同类型有不同显示 */}
        {(subscription.status === 'active' || subscription.subscription_status === 'active' || isInTrialPeriod) && (
          <ButtonGroup>
            {isInTrialPeriod ? (
              <ActionButton primary onClick={handleViewPlans}>
                {language === 'zh' ? '升级到专业版' : 'Upgrade to Pro'}
              </ActionButton>
            ) : isLifetime ? (
              /* 终身版本不需要管理订阅按钮 */
              <ActionButton onClick={handleViewPlans}>
                {t('viewOtherPlans')}
              </ActionButton>
            ) : (
              <ActionButton primary onClick={handleManageSubscription} disabled={redirectLoading}>
                {redirectLoading ? t('redirecting') : t('manageSubscription')}
              </ActionButton>
            )}
          </ButtonGroup>
        )}
      </SubscriptionCard>
    </Container>
  );
};

export default MySubscription; 